import { useState } from "react";
import { CheckCircle2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TableShell, TH } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { AccountSelect, accountLabel, money, TaxRateSelect, useTaxRates } from "./shared";
import type { BankLineGroup, LedgerAccount } from "./types";

/** Uncategorised lines from one payee, one decision for all of them (optionally kept as a rule). Across every account and date. */
export function BankLineGroups({
  chart,
  readOnly,
  onChanged,
}: {
  chart: LedgerAccount[];
  readOnly: boolean;
  onChanged: () => Promise<void>;
}) {
  const { books, canManage } = useBooks();
  const groups = useLoad(() => books.GET("/v1/books/current/bank-lines/groups", {}), [books]);
  const [open, setOpen] = useState<BankLineGroup | null>(null);
  const names = new Map(chart.map((a) => [a.id, a]));

  if (groups.loading && !groups.data) return <LoadingState label="Grouping transactions by payee" />;
  if (groups.error) return <ErrorState message={apiErrorMessage(groups.error, "Couldn't load the groups.")} onRetry={groups.reload} />;
  const list = groups.data ?? [];
  if (list.length === 0) {
    return (
      <EmptyState icon={CheckCircle2} title="No payees to group">
        Lines waiting for review show here when two or more come from the same payee, in any account or month.
      </EmptyState>
    );
  }

  return (
    <>
      <p className="mb-2 text-xs text-muted-foreground">Every account and month, largest total first.</p>
      <TableShell>
        <Table>
          <TableHeader>
            <HeaderRow>
              <TableHead className={TH}>Payee</TableHead>
              <TableHead className={cn(TH, "text-right")}>Total</TableHead>
              <TableHead className={TH}>Suggested account</TableHead>
              {!readOnly && canManage ? <TableHead className={TH} /> : null}
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {list.map((g) => {
              const suggested = g.suggestedLedgerAccountId ? names.get(g.suggestedLedgerAccountId) : undefined;
              return (
                <TableRow key={`${g.key}-${g.direction}`}>
                  <TableCell className="w-full max-w-0">
                    <div className="truncate font-medium">{g.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {g.count} transactions · money {g.direction === "In" ? "in" : "out"}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{money(g.total)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {suggested ? accountLabel(suggested) : <span className="text-muted-foreground">None yet</span>}
                  </TableCell>
                  {!readOnly && canManage ? (
                    <TableCell className="w-px text-right">
                      <Button size="sm" onClick={() => setOpen(g)}>
                        Categorise all
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableShell>
      {!canManage ? <p className="mt-2 text-xs text-muted-foreground">Owners and admins can categorise a whole group.</p> : null}
      <Sheet open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-lg">
          {open ? (
            <CategoriseGroup
              key={`${open.key}-${open.direction}`}
              group={open}
              chart={chart}
              onDone={async () => {
                setOpen(null);
                await Promise.all([groups.reload(), onChanged()]);
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

function CategoriseGroup({ group, chart, onDone }: { group: BankLineGroup; chart: LedgerAccount[]; onDone: () => Promise<void> }) {
  const { books } = useBooks();
  const taxRates = useTaxRates();
  const [accountId, setAccountId] = useState<string | null>(group.suggestedLedgerAccountId ?? null);
  const [taxRateId, setTaxRateId] = useState<string | null>(group.suggestedAccountingTaxRateId ?? null);
  const [createRule, setCreateRule] = useState(true);
  const [ruleName, setRuleName] = useState(group.name);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function save() {
    if (!accountId) return;
    setBusy(true);
    setFailure(null);
    const { data, error } = await books.POST("/v1/books/current/bank-lines/groups/categorise", {
      body: {
        key: group.key,
        direction: group.direction,
        ledgerAccountId: accountId,
        accountingTaxRateId: taxRateId,
        createRule,
        ruleName: createRule ? ruleName.trim() || null : null,
      },
    });
    setBusy(false);
    if (error) {
      const message = apiErrorMessage(error, "Couldn't categorise the group.");
      setFailure(message);
      toast.error(message);
      return;
    }
    toast.success(`${data.categorised} transactions categorised${data.rule ? `, and future ones will be too` : ""}.`);
    await onDone();
  }

  return (
    <>
      <SheetHeader className="border-b">
        <SheetTitle>{group.name}</SheetTitle>
        <SheetDescription>
          {group.count} transactions waiting for review · money {group.direction === "In" ? "in" : "out"} · {money(group.total)}
        </SheetDescription>
      </SheetHeader>
      <div className="space-y-4 p-4">
        {failure ? (
          <p role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            {failure}
          </p>
        ) : null}
        <AccountSelect
          id="group-account"
          label="Account"
          value={accountId}
          accounts={chart.filter((a) => a.isActive !== false)}
          onChange={setAccountId}
        />
        <div className="space-y-1.5">
          <Label htmlFor="group-vat">VAT</Label>
          <TaxRateSelect id="group-vat" value={taxRateId} rates={taxRates.data ?? []} onChange={setTaxRateId} />
        </div>
        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex items-start gap-2">
            <Checkbox id="group-rule" checked={createRule} onCheckedChange={(c) => setCreateRule(c === true)} className="mt-0.5" />
            <Label htmlFor="group-rule" className="flex-col items-start gap-0.5 font-normal">
              <span className="font-medium">Always do this</span>
              <span className="text-xs text-muted-foreground">
                Saves a rule, so new transactions from {group.name} are categorised the same way as they arrive.
              </span>
            </Label>
          </div>
          {createRule ? (
            <div className="space-y-1.5 pl-6">
              <Label htmlFor="group-rule-name">Rule name</Label>
              <Input id="group-rule-name" value={ruleName} maxLength={128} onChange={(e) => setRuleName(e.target.value)} />
            </div>
          ) : null}
        </div>
        <Button onClick={save} disabled={!accountId || busy}>
          {busy ? <Spinner className="size-4" /> : null} Categorise {group.count} transactions
        </Button>
      </div>
    </>
  );
}
